// What a stored profile holds, and what the solver reads from it.
//
// A blank answer is stored as an absent field, never as a default. Writing the
// default into storage made every profile look answered after its first save,
// so the planner stopped asking the questions it exists to ask. Defaults are
// applied here, at read time, for the solver and the utility calculation only.

import type { GoalPriority, Profile, StaminaCurve, SubgenreWeight } from '../engine/types.ts';

type Answered = 'staminaCurve' | 'goalPriority' | 'alignmentIntent';

export type StoredProfile = Omit<Profile, Answered> & Partial<Pick<Profile, Answered>>;

// `null` or `undefined` on a key that is present means the answer was cleared.
// A key that is absent leaves the stored answer alone.
export interface PreferencePatch {
  subgenreWeights?: Record<string, SubgenreWeight>;
  goalPriorities?: GoalPriority[] | null;
  staminaCurve?: StaminaCurve | null;
  alignmentIntent?: Profile['alignmentIntent'] | null;
}

export const BLANK: Omit<Profile, 'userId'> = {
  subgenreWeights: {},
  energyPref: 0.5,
  staminaCurve: 'all_three_nights',
  crowdTolerance: 0.5,
  goalPriority: 'max_music',
  alignmentIntent: 'solo',
  alignedWith: [],
};

/** The profile with blank answers filled from defaults, for the solver and utility. */
export function readProfileForSolver(profile: StoredProfile | undefined, userId = profile?.userId ?? 'me'): Profile {
  return {
    ...BLANK,
    ...profile,
    userId,
    staminaCurve: profile?.staminaCurve ?? BLANK.staminaCurve,
    goalPriority: profile?.goalPriority ?? BLANK.goalPriority,
    alignmentIntent: profile?.alignmentIntent ?? BLANK.alignmentIntent,
  };
}

/** The profile to store after one patch. Blank answers stay absent. */
export function mergePreferences(stored: StoredProfile | undefined, patch: PreferencePatch, userId: string): StoredProfile {
  const next: StoredProfile = {
    subgenreWeights: {},
    energyPref: BLANK.energyPref,
    crowdTolerance: BLANK.crowdTolerance,
    alignedWith: [],
    ...stored,
    userId,
  };
  if ('subgenreWeights' in patch) next.subgenreWeights = patch.subgenreWeights ?? {};
  if ('staminaCurve' in patch) {
    if (patch.staminaCurve) next.staminaCurve = patch.staminaCurve;
    else delete next.staminaCurve;
  }
  if ('alignmentIntent' in patch) {
    if (patch.alignmentIntent) next.alignmentIntent = patch.alignmentIntent;
    else delete next.alignmentIntent;
  }
  // The first ranked goal is also written to the single field, so an older
  // reader and a newer one agree.
  if ('goalPriorities' in patch) {
    const goals = patch.goalPriorities ?? [];
    if (goals.length > 0) {
      next.goalPriorities = goals;
      next.goalPriority = goals[0];
    } else {
      delete next.goalPriorities;
      delete next.goalPriority;
    }
  }
  return next;
}
